import z from "zod";
import { ProjectTypeEnum } from "../../projects";
import { ModelBackendEnum, ModelOutputTypeEnum, ModelQuantizationEnum, suggestionWarningSchema, SuggestionWarningSeverityEnum, } from "../model.schema";
import { buildParamValueSchema, getHyperparamRegistryEntry, } from "./registry";
export const suggestHyperparamsRequestSchema = z.object({
    backend: z.enum(ModelBackendEnum).default(ModelBackendEnum.ULTRA_VISION),
    taskIds: z.number().array().default([]),
    labelIds: z.number().array().default([]),
    trainingType: z.enum(ProjectTypeEnum),
    annotationsUsed: z.enum(ProjectTypeEnum).array(),
    epochs: z.number().int().positive().nullable().default(null),
    currentHyperparams: z.record(z.string(), z.unknown()).default({}),
    splitTrain: z.number(),
    splitValidate: z.number(),
    splitTest: z.number(),
    quantization: z.enum(ModelQuantizationEnum),
    outputTypes: z.enum(ModelOutputTypeEnum).array().default([]),
    useGroups: z.boolean().default(false),
});
export const suggestedParamSchema = z.object({
    value: z.union([z.string(), z.number(), z.boolean()]),
    reasoning: z.string(),
});
export const suggestedEpochsSchema = z.object({
    value: z.number().int().positive(),
    reasoning: z.string(),
});
export const suggestedSplitSchema = z
    .object({
    train: z.number().int().min(0).max(100),
    validation: z.number().int().min(0).max(100),
    test: z.number().int().min(0).max(100),
    reasoning: z.string(),
})
    .refine((s) => s.train + s.validation + s.test === 100, {
    message: "train + validation + test must equal exactly 100",
});
export const suggestedLabelsSchema = z.object({
    labelIds: z.number().array(),
    reasoning: z.string(),
});
export const suggestHyperparamsResponseSchema = z.object({
    backend: z.enum(ModelBackendEnum),
    epochs: suggestedEpochsSchema,
    split: suggestedSplitSchema,
    params: z.record(z.string(), suggestedParamSchema),
    labels: suggestedLabelsSchema,
    warnings: suggestionWarningSchema.array(),
    summary: z.string(),
    sampledTaskIds: z.number().array(),
});
export const getGeminiSuggestionSchema = (backend, trainingType, selectableLabelNames = []) => {
    const entry = getHyperparamRegistryEntry(backend, trainingType);
    if (!entry) {
        throw new Error(`No hyperparam suggestion registry entry for ${backend} / ${trainingType}`);
    }
    const labelSelection = z
        .object({
        selected: (selectableLabelNames.length > 0
            ? z.array(z.enum(selectableLabelNames))
            : z.array(z.string())).describe("Labels to include in training — a subset of the labels listed as available. Exclude a label ONLY when including it would clearly hurt the model (e.g. no or almost no annotated examples). Add a warning entry explaining every exclusion."),
        reasoning: z
            .string()
            .describe("One short sentence explaining the label selection"),
    })
        .optional();
    const paramShape = Object.fromEntries(Object.entries(entry.params)
        .filter(([, def]) => def.suggestible !== false)
        .map(([name, def]) => [
        name,
        z
            .object({
            value: buildParamValueSchema(def, { forSuggestion: true }),
            reasoning: z
                .string()
                .describe("One short sentence explaining this choice"),
        })
            .optional(),
    ]));
    return z.object({
        epochs: z.object({
            value: z
                .number()
                .int()
                .min(1)
                .max(1000)
                .describe("Total training epochs, sized to the dataset (small datasets converge in fewer epochs but benefit from patience; very small ones overfit past ~150)."),
            reasoning: z.string().describe("One short sentence explaining this choice"),
        }),
        split: z
            .object({
            train: z
                .number()
                .int()
                .min(0)
                .max(100)
                .describe("Training set percentage of the dataset"),
            validation: z
                .number()
                .int()
                .min(0)
                .max(100)
                .describe("Validation set percentage"),
            test: z
                .number()
                .int()
                .min(0)
                .max(100)
                .describe("Test set percentage; 0 is acceptable for small datasets"),
            reasoning: z
                .string()
                .describe("One short sentence explaining this choice"),
        })
            .describe("Suggested train/validation/test dataset split. The three integer percentages MUST sum to exactly 100.")
            .refine((s) => s.train + s.validation + s.test === 100, {
            message: "train + validation + test must equal exactly 100",
        }),
        params: z.object(paramShape),
        labels: labelSelection,
        warnings: z
            .array(z.object({
            severity: z
                .enum(SuggestionWarningSeverityEnum)
                .describe("critical = training likely to fail or produce an unusable model; warning = quality risk; info = advisory"),
            message: z.string().describe("Actionable, user-facing message"),
            affectedLabels: z
                .array(z.string())
                .nullable()
                .describe("Label names this warning concerns, or null"),
        }))
            .describe("Dataset/setup problems derived from the analytics: too few images, class imbalance, labels without examples, tiny objects vs image size, epochs mismatch, split issues, etc. Empty if none."),
        summary: z
            .string()
            .describe("2-3 sentence overall rationale for the suggested configuration"),
    });
};
//# sourceMappingURL=hyperparam-suggestion.schema.js.map